import { useEffect, useRef, useState, useCallback } from 'react';

interface SSEMessage {
  type: string;
  data: any;
  timestamp?: number;
}

interface UseSSEProps {
  url: string;
  onMessage?: (message: SSEMessage) => void;
  onOpen?: () => void;
  onClose?: () => void;
  onError?: (error: Event) => void;
  reconnectInterval?: number;
  maxReconnectAttempts?: number;
}

export const useSSE = ({
  url,
  onMessage,
  onOpen,
  onClose,
  onError,
  reconnectInterval = 3000,
  maxReconnectAttempts = 10
}: UseSSEProps) => {
  const eventSource = useRef<EventSource | null>(null);
  const [connectionStatus, setConnectionStatus] = useState<'Connecting' | 'Open' | 'Closed'>('Closed');
  const [reconnectAttempts, setReconnectAttempts] = useState(0);
  const reconnectTimeout = useRef<NodeJS.Timeout | null>(null);
  const lastHeartbeat = useRef<number>(Date.now());
  const heartbeatTimeout = useRef<NodeJS.Timeout | null>(null);
  const isManualClose = useRef<boolean>(false);

  const clearTimeouts = useCallback(() => {
    if (reconnectTimeout.current) {
      clearTimeout(reconnectTimeout.current);
      reconnectTimeout.current = null;
    }
    if (heartbeatTimeout.current) {
      clearTimeout(heartbeatTimeout.current);
      heartbeatTimeout.current = null;
    }
  }, []);

  const checkHeartbeat = useCallback(() => {
    const now = Date.now();
    const timeSinceLastHeartbeat = now - lastHeartbeat.current;
    
    // 如果超过60秒没有收到心跳，认为连接可能有问题
    if (timeSinceLastHeartbeat > 60000 && connectionStatus === 'Open') {
      console.warn('SSE心跳超时，尝试重连...');
      if (eventSource.current) {
        eventSource.current.close();
      }
    } else if (connectionStatus === 'Open') {
      // 继续检查心跳
      heartbeatTimeout.current = setTimeout(checkHeartbeat, 30000);
    }
  }, [connectionStatus]);

  const connect = useCallback(() => {
    // 如果是手动关闭，不要重连
    if (isManualClose.current) {
      return;
    }

    if (eventSource.current) {
      eventSource.current.close();
    }

    clearTimeouts();
    setConnectionStatus('Connecting');
    
    console.log(`尝试建立SSE连接... (尝试次数: ${reconnectAttempts + 1})`);
    
    try {
      eventSource.current = new EventSource(url);

      eventSource.current.onopen = () => {
        console.log('✅ SSE连接已建立');
        setConnectionStatus('Open');
        setReconnectAttempts(0); // 重置重连次数
        lastHeartbeat.current = Date.now();
        
        // 开始心跳检查
        heartbeatTimeout.current = setTimeout(checkHeartbeat, 30000);
        
        onOpen?.();
      };

      eventSource.current.onmessage = (event) => {
        try {
          const message: SSEMessage = JSON.parse(event.data);
          
          // 处理心跳消息
          if (message.type === 'heartbeat') {
            lastHeartbeat.current = Date.now();
            console.debug('📡 收到SSE心跳');
            return;
          }
          
          // 处理连接确认消息
          if (message.type === 'connected') {
            console.log('📡 SSE连接确认:', message.data);
            return;
          }
          
          // 处理服务器关闭消息
          if (message.type === 'server_shutdown') {
            console.warn('⚠️ 服务器即将关闭:', message.data);
            return;
          }
          
          // 处理其他消息
          console.log('📡 收到SSE消息:', message);
          onMessage?.(message);
          
        } catch (error) {
          console.error('❌ 解析SSE消息失败:', error, 'Raw data:', event.data);
        }
      };

      eventSource.current.onerror = (error) => {
        console.error('❌ SSE连接错误:', error);
        setConnectionStatus('Closed');
        clearTimeouts();
        onError?.(error);

        // 自动重连逻辑
        if (!isManualClose.current && reconnectAttempts < maxReconnectAttempts) {
          setReconnectAttempts(prev => {
            const nextAttempt = prev + 1;
            const delay = Math.min(reconnectInterval * Math.pow(1.5, nextAttempt - 1), 30000); // 指数退避，最大30秒
            console.log(`⏰ ${delay/1000}秒后尝试重连 (${nextAttempt}/${maxReconnectAttempts})`);
            
            reconnectTimeout.current = setTimeout(() => {
              connect();
            }, delay);
            
            return nextAttempt;
          });
        } else if (reconnectAttempts >= maxReconnectAttempts) {
          console.error('❌ SSE重连次数已达上限，停止重连');
        }
      };

    } catch (error) {
      console.error('❌ 创建SSE连接失败:', error);
      setConnectionStatus('Closed');
    }
  }, [url, reconnectAttempts, maxReconnectAttempts, reconnectInterval, onMessage, onOpen, onClose, onError, clearTimeouts, checkHeartbeat, connectionStatus]);

  useEffect(() => {
    isManualClose.current = false;
    connect();

    return () => {
      isManualClose.current = true;
      clearTimeouts();
      if (eventSource.current) {
        eventSource.current.close();
      }
    };
  }, [url]); // 只依赖url，避免重连循环

  const manualReconnect = useCallback(() => {
    isManualClose.current = false;
    setReconnectAttempts(0);
    connect();
  }, [connect]);

  const disconnect = useCallback(() => {
    isManualClose.current = true;
    clearTimeouts();
    if (eventSource.current) {
      eventSource.current.close();
    }
    setConnectionStatus('Closed');
  }, [clearTimeouts]);

  return {
    connectionStatus,
    reconnectAttempts,
    reconnect: manualReconnect,
    disconnect,
    isConnected: connectionStatus === 'Open'
  };
}; 